// Loyalty Phase 1 (Sep 2026) — the tenant-admin view of one member's
// points, and the owner-only manual adjustment control.
//
// Rendered inside the existing member drawer rather than as a second
// place to look for a member. It shows the operational detail the
// customer-facing view deliberately omits: reserved points, the rule
// version each movement was made under, the order/payment/refund the
// movement came from, who made a manual adjustment and why, and whether
// anything is still pending or needs reconciliation.
//
// Manual adjustment is OWNER-ONLY, and the server enforces that against
// `companies.owner_id` independently. Non-owners see the balance and the
// history; the controls are not rendered for them at all, so there is no
// disabled button implying a permission they might obtain by asking.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  describeLedgerEntry,
  describeLedgerAfter,
} from "../../lib/loyaltyLedgerDisplay";

// Ledger display correction (Sep 2026) — the label/amount/units for each
// entry type now come from `lib/loyaltyLedgerDisplay.js`, because the
// correct field to read DEPENDS on the entry type and this component was
// reading `points_delta` for all of them. A `redeem_reserved` entry
// carries its point count in `reserved_delta` and deliberately has
// `points_delta = 0`, so the old rendering printed "Held for order 0"
// for a reservation that had held a point perfectly correctly.
//
// Stored ledger records are untouched — this is interpretation only.
const TONE_CLASS = {
  credit: "font-semibold text-emerald-700",
  debit: "font-semibold text-slate-800",
  hold: "font-semibold text-amber-700",
  neutral: "font-semibold text-slate-500",
};

// A durable, client-generated request id, minted ONCE when the form
// opens and reused on every retry of that same adjustment.
//
// This is the whole idempotency story for manual adjustments: the server
// keys the ledger operation on it, so a double-click, a lost response, or
// a retry after a network blip applies exactly one movement. Deriving it
// from actor + member + a timestamp would be wrong in both directions —
// it would collapse two legitimately identical adjustments made minutes
// apart, and would fail to deduplicate a retry that crossed a second
// boundary.
const newRequestId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `adj-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function MemberLoyaltySection({ customerId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Adjustment form.
  const [open, setOpen] = useState(false);
  const [requestId, setRequestId] = useState(newRequestId);
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [allowNegative, setAllowNegative] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmNegative, setConfirmNegative] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: d } = await api.get(`/commerce/members/${customerId}/loyalty`);
      setData(d);
    } catch (e) {
      setError(e.response?.data?.detail?.message || "Failed to load points");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setOpen(false);
    setDelta("");
    setReason("");
    setAllowNegative(false);
    setConfirmNegative(false);
    // A NEW request id for the next adjustment — reusing the previous one
    // would make a second, genuinely different adjustment a silent no-op.
    setRequestId(newRequestId());
  };

  const submit = async () => {
    const points = parseInt(delta, 10);
    if (!points || Number.isNaN(points)) {
      toast.error("Enter a non-zero whole number of points");
      return;
    }
    if (!reason.trim()) {
      toast.error("A reason is required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/commerce/members/${customerId}/loyalty/adjust`, {
        request_id: requestId,
        points_delta: points,
        reason: reason.trim(),
        allow_negative: allowNegative,
      });
      toast.success(points > 0 ? `Added ${points} points` : `Removed ${-points} points`);
      resetForm();
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || {};
      if (detail.error === "would_go_negative") {
        // The server refused because this debit crosses zero. Surface the
        // explicit confirmation rather than silently retrying with
        // allow_negative — going into debt is the owner's decision.
        setConfirmNegative(true);
      } else {
        toast.error(detail.message || "Adjustment failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const isOwner = !!data?.is_owner;
  const healthy = data?.health?.healthy !== false;
  const history = data?.history?.items || [];

  return (
    <div className="px-5 py-4 border-t border-slate-200" data-testid="member-loyalty-section">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">Loyalty points</div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs underline text-slate-500 hover:text-slate-800 disabled:opacity-40"
          data-testid="member-loyalty-refresh"
        >
          Refresh
        </button>
      </div>

      {loading && (
        <div className="py-4 text-sm text-slate-500" data-testid="member-loyalty-loading">
          Loading points…
        </div>
      )}

      {!loading && error && (
        <div className="py-3" data-testid="member-loyalty-error">
          <div className="text-sm text-slate-600">{error}</div>
          <button
            type="button"
            onClick={load}
            className="mt-1 text-xs underline text-slate-600"
            data-testid="member-loyalty-retry"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {!healthy && (
            <div
              className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="member-loyalty-unhealthy"
            >
              Points are read-only right now: {data.health?.reason || "the loyalty subsystem is unavailable."}{" "}
              Balances and history are intact.
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded bg-slate-50 border border-slate-200 py-2">
              <div className="text-lg font-semibold" data-testid="member-loyalty-balance">
                {data.balance_points}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Balance</div>
            </div>
            <div className="rounded bg-slate-50 border border-slate-200 py-2">
              <div className="text-lg font-semibold" data-testid="member-loyalty-reserved">
                {data.reserved_points}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Held</div>
            </div>
            <div className="rounded bg-slate-50 border border-slate-200 py-2">
              <div className="text-lg font-semibold" data-testid="member-loyalty-available">
                {data.available_points}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">Available</div>
            </div>
          </div>

          {data.is_negative && (
            <div
              className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="member-loyalty-negative"
            >
              Negative balance. A refund reversed points that had already been spent.
              Redemption is blocked until this returns to zero.
            </div>
          )}

          {isOwner && healthy && (
            <div className="mt-3">
              {!open ? (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="text-xs font-semibold text-emerald-700 underline"
                  data-testid="member-loyalty-adjust-open"
                >
                  Adjust points
                </button>
              ) : (
                <div className="rounded border border-slate-200 p-3 space-y-2" data-testid="member-loyalty-adjust-form">
                  <label className="block text-xs font-medium" htmlFor="loyalty-adjust-delta">
                    Points (negative to remove)
                  </label>
                  <input
                    id="loyalty-adjust-delta"
                    type="number"
                    value={delta}
                    onChange={(e) => { setDelta(e.target.value); setConfirmNegative(false); }}
                    className="w-full rounded border px-2 py-1 text-sm"
                    data-testid="member-loyalty-adjust-delta"
                  />
                  <label className="block text-xs font-medium" htmlFor="loyalty-adjust-reason">
                    Reason (required — shown in the member's history)
                  </label>
                  <input
                    id="loyalty-adjust-reason"
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full rounded border px-2 py-1 text-sm"
                    data-testid="member-loyalty-adjust-reason"
                  />

                  {confirmNegative && (
                    <div
                      className="rounded border border-amber-300 bg-amber-50 px-2 py-2 text-xs text-amber-900"
                      data-testid="member-loyalty-adjust-negative-confirm"
                    >
                      This takes the member below zero points.
                      <label className="mt-1.5 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={allowNegative}
                          onChange={(e) => setAllowNegative(e.target.checked)}
                          data-testid="member-loyalty-adjust-allow-negative"
                        />
                        <span>Yes, allow a negative balance.</span>
                      </label>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={submit}
                      disabled={busy || (confirmNegative && !allowNegative)}
                      className="rounded bg-emerald-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                      data-testid="member-loyalty-adjust-submit"
                    >
                      {busy ? "Saving…" : "Apply adjustment"}
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      disabled={busy}
                      className="text-xs underline text-slate-500"
                      data-testid="member-loyalty-adjust-cancel"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              Ledger
            </div>
            {history.length === 0 ? (
              <div className="py-3 text-sm text-slate-500" data-testid="member-loyalty-history-empty">
                No points activity yet.
              </div>
            ) : (
              <ul className="divide-y text-xs" data-testid="member-loyalty-history">
                {history.map((h) => {
                  const view = describeLedgerEntry(h);
                  return (
                  <li key={h.id} className="py-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium" data-testid={`member-loyalty-entry-label-${h.id}`}>
                          {view.label}
                        </div>
                        {view.detail && (
                          <div
                            className="text-slate-600"
                            data-testid={`member-loyalty-entry-detail-${h.id}`}
                          >
                            {view.detail}
                          </div>
                        )}
                        <div className="text-slate-500">
                          {h.created_at ? new Date(h.created_at).toLocaleString() : ""}
                        </div>
                        {h.order_code && (
                          <div className="text-slate-500 font-mono">Order {h.order_code}</div>
                        )}
                        {h.refund_id && (
                          <div className="text-slate-500 font-mono">
                            Refund {String(h.refund_id).slice(0, 8)}…
                          </div>
                        )}
                        {h.reason && <div className="text-slate-600 italic">{h.reason}</div>}
                        {h.actor_email && (
                          <div className="text-slate-500">by {h.actor_email}</div>
                        )}
                        <div className="text-slate-400">
                          rule v{h.rule_version ?? "—"}
                          {h.seq != null && <> · #{h.seq}</>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div
                          className={TONE_CLASS[view.tone] || TONE_CLASS.neutral}
                          data-testid={`member-loyalty-entry-amount-${h.id}`}
                        >
                          {view.amountText}
                        </div>
                        {/* Balance and held are separate pools: a hold
                            never reduces the balance, and only
                            finalization does. Showing one number for
                            both is what made a hold look like a debit. */}
                        <div
                          className="text-slate-400"
                          data-testid={`member-loyalty-entry-after-${h.id}`}
                        >
                          {describeLedgerAfter(h)}
                        </div>
                      </div>
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}
